import { useTranslation } from 'react-i18next';
import { Building2, CircleAlert, LoaderCircle, LogIn } from 'lucide-react';
import { useErrorText } from '@/lib/error-text';
import { useSession } from '@/resources/auth/auth.service';
import { useAcceptInvite, useInvitePreview } from '@/resources/invite/invite.service';
import { AuthPanel } from '@/auth/auth-panel';
import { AuthShell } from '@/auth/auth-shell';
import { useRoleLabels } from '@/dashboard/ui';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

export function JoinPage({ token, onDone }: { token: string; onDone: () => void }) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const roles = useRoleLabels();

  const preview = useInvitePreview(token);
  const session = useSession();
  const accept = useAcceptInvite();

  function join() {
    accept.mutate(token, { onSuccess: onDone });
  }

  return (
    <AuthShell>
      <Card className="rise w-full max-w-sm shadow-lg [--card-spacing:--spacing(6)]">
        <CardHeader className="justify-items-center text-center">
          <img
            src="/assets/logo-nargus.png"
            alt="nargus"
            className="h-12 w-auto mix-blend-multiply dark:mix-blend-screen dark:invert"
          />
        </CardHeader>

        <CardContent className="flex flex-col gap-5">
          {preview.isPending ? (
            <p className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
              <LoaderCircle className="size-4 animate-spin" />
              {t('join.checking')}
            </p>
          ) : preview.isError ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CircleAlert className="size-6 text-destructive" />
              <p className="text-sm">{errorText(preview.error)}</p>
              <Button variant="outline" onClick={onDone}>
                {t('join.toLogin')}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center gap-2 text-center">
                <span className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Building2 className="size-5" />
                </span>
                <p className="text-sm text-muted-foreground">{t('join.invitedTo')}</p>
                <p className="font-heading text-lg font-medium">
                  {preview.data?.organizationName}
                </p>
                {preview.data && (
                  <Badge variant="secondary">
                    {t('join.asRole', { role: roles[preview.data.role] })}
                  </Badge>
                )}
              </div>

              {session.isLoading ? (
                <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <LoaderCircle className="size-4 animate-spin" />
                  {t('common.oneMoment')}
                </p>
              ) : session.user ? (
                <div className="flex flex-col gap-2">
                  <p className="text-center text-sm text-muted-foreground">
                    {t('join.signedInAs', { email: session.user.email })}
                  </p>
                  <Button onClick={join} disabled={accept.isPending}>
                    {accept.isPending ? (
                      <LoaderCircle className="size-4 animate-spin" />
                    ) : (
                      <LogIn className="size-4" />
                    )}
                    {accept.isPending ? t('join.joining') : t('join.join')}
                  </Button>
                  <Button variant="ghost" onClick={onDone} disabled={accept.isPending}>
                    {t('common.cancel')}
                  </Button>
                  {accept.error && (
                    <p className="flex items-center gap-1.5 text-sm text-destructive">
                      <CircleAlert className="size-4 shrink-0" />
                      {errorText(accept.error)}
                    </p>
                  )}
                </div>
              ) : (
                <AuthPanel
                  submitLabel={{
                    login: t('join.submitLogin'),
                    register: t('join.submitRegister'),
                  }}
                  onAuthenticated={join}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
